import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlarmClockOff, Clock3, FileText, ChevronLeft, HelpCircle } from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export default function SchoolExpired() {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/school/budgets/expired");
      setBudgets(r.data || []);
    } catch (e) {
      toast.error("Erro ao carregar orçamentos vencidos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendReopen = async () => {
    if (!active) return;
    setSending(true);
    try {
      const r = await api.post(`/school/budgets/${active.id}/request-reopen`, { reason });
      if (r.data?.already) {
        toast.info("Já existe uma solicitação pendente para este orçamento.");
      } else {
        toast.success("Solicitação de reabertura enviada!");
      }
      setActive(null);
      setReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao solicitar reabertura");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5 pb-24">
      <div className="flex items-center gap-3">
        <Link to="/escola" className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 text-sm">
          <ChevronLeft className="w-4 h-4" /> Voltar
        </Link>
      </div>

      <div className="bg-gradient-to-br from-orange-500 to-amber-600 text-white rounded-2xl p-5 shadow-md">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
            <AlarmClockOff className="w-6 h-6" strokeWidth={2.4} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-xl font-black">Orçamentos vencidos</h1>
            <p className="text-[13px] text-white/90 mt-1">
              Orçamentos cujo prazo de aprovação expirou. Você pode solicitar a reabertura caso ainda tenha interesse — a AMC poderá reavaliar os valores e enviar novamente.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-slate-500">Carregando…</div>
      ) : budgets.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
            <AlarmClockOff className="w-7 h-7 text-emerald-600" />
          </div>
          <p className="font-semibold text-slate-700">Nenhum orçamento vencido</p>
          <p className="text-xs text-slate-500 mt-1">Todos os seus orçamentos estão dentro do prazo de validade.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {budgets.map((b) => (
            <div
              key={b.id}
              data-testid={`expired-card-${b.number}`}
              className="bg-white rounded-2xl border border-orange-100 p-4 shadow-sm"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-orange-700" strokeWidth={2.3} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-heading font-black text-[#1e3a8a]">Nº {b.number}</p>
                    <span className="text-xs font-semibold text-orange-700 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">
                      Vencido
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    <Clock3 className="inline w-3 h-3 mr-0.5" />
                    Validade: <strong className="text-slate-700">{formatDateBR(b.validity_date)}</strong>
                  </p>
                  {b.service_type && (
                    <p className="text-sm text-slate-700 mt-1 line-clamp-2">{b.service_type}</p>
                  )}
                  <p className="text-sm font-bold text-slate-900 mt-1 tabular-nums">{BRL(b.total || 0)}</p>
                </div>
              </div>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <Link
                  to={`/escola/orcamentos/${b.id}`}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Ver orçamento
                </Link>
                <Button
                  size="sm"
                  disabled={b.reopen_requested}
                  onClick={() => setActive(b)}
                  data-testid={`btn-request-reopen-${b.number}`}
                  className={`flex-1 ${b.reopen_requested ? "bg-slate-300" : "bg-orange-600 hover:bg-orange-700"} text-white`}
                >
                  {b.reopen_requested ? "Solicitação enviada" : "Solicitar reabertura"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(v) => { if (!v) { setActive(null); setReason(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-orange-600" />
              Solicitar reabertura
            </DialogTitle>
            <DialogDescription>
              Explique brevemente por que ainda tem interesse neste serviço. A AMC vai avaliar e, se possível, reenviar o orçamento com uma nova validade.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea
              rows={4}
              placeholder="Ex: Verba foi liberada agora, podemos aprovar esta semana…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="input-reopen-reason"
            />
            <p className="text-[11px] text-slate-500">Opcional, mas ajuda a AMC a priorizar.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActive(null); setReason(""); }}>Cancelar</Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700 text-white"
              disabled={sending}
              onClick={sendReopen}
              data-testid="btn-send-reopen"
            >
              {sending ? "Enviando…" : "Enviar solicitação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
